import { useCallback, useState } from 'react';

interface Props {
  body: string;
  /** Optional derived filename (without extension). Overrides derivation. */
  filenameHint?: string;
}

/**
 * Derive a filename slug from the body's first Markdown heading, or a
 * timestamped fallback when no heading is present.
 */
function deriveFilename(body: string): string {
  const m = body.match(/^#\s+(.+)$/m);
  if (m) {
    const slug = m[1]
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
    if (slug) return slug;
  }
  const now = new Date();
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return (
    `document-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}`
  );
}

async function writeToClipboardFallback(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      /* fall through to execCommand */
    }
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    ta.remove();
  }
}

function triggerDownload(body: string, filename: string): void {
  const blob = new Blob([body], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/**
 * Stage 28: floating Copy + Save-as-.md buttons overlaid on a ```markdown
 * code block in ChatTranscript. The parent <pre> must have
 * position: relative for the absolute positioning to anchor correctly.
 */
export function CodeBlockActions({ body, filenameHint }: Props): JSX.Element {
  const [toast, setToast] = useState<string | null>(null);

  const onCopy = useCallback(async () => {
    try {
      await writeToClipboardFallback(body);
      setToast('Copied');
    } catch {
      setToast('Copy failed');
    }
    setTimeout(() => setToast(null), 1500);
  }, [body]);

  const onSave = useCallback(() => {
    const name = `${filenameHint ?? deriveFilename(body)}.md`;
    triggerDownload(body, name);
  }, [body, filenameHint]);

  return (
    <>
      {toast ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            background: 'var(--accent, #3a6)',
            color: 'white',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: '0.75em',
            zIndex: 2,
          }}
        >
          {toast}
        </div>
      ) : (
        <div
          className="code-block-actions"
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            display: 'flex',
            gap: 4,
            zIndex: 1,
          }}
        >
          <button
            type="button"
            onClick={onCopy}
            aria-label="Copy markdown block"
            title="Copy"
            style={{
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: '0.75em',
              background: 'var(--bg-subtle, #222)',
              border: '1px solid var(--border, #333)',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            📋 Copy
          </button>
          <button
            type="button"
            onClick={onSave}
            aria-label="Download block as markdown file"
            title="Save as .md"
            style={{
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: '0.75em',
              background: 'var(--bg-subtle, #222)',
              border: '1px solid var(--border, #333)',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            💾 .md
          </button>
        </div>
      )}
    </>
  );
}
